/**
 * "My review" — the self-service half of `/hr/performance`, WP-W8.
 * `web/shapes/hr-performance.md` §5 is the agreed shape.
 *
 * Three independent regions (`PRODUCT.md` §4): the caller's own **reviews**, their own
 * **goals**, and a **self-review** form. They are independent on purpose — an employee
 * holding `hr.view_own_review` but not `hr.submit_review` still reads both lists while
 * the self-review submit renders its own `403` in place (`web/shapes/hr-performance.md`
 * §3), exactly the floor/action split `MyLeavePanel` already keeps for leave.
 *
 * The self-review form has **no `employee_id` field and no reviewer field of any kind**.
 * `kind` is fixed to `'self'` — not a control a person sets — and `cycle_id` is a typed
 * field rather than a picker: `GET /hr/performance/review-cycles` is gated on
 * `hr.view_reviews`, a code this floor does not carry, so there is no cycle directory
 * this form could offer (§3).
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { Goal, Review, ReviewSubmitRequest } from '../../api/hr';
import { GOAL_STATUS_BADGE, GOAL_STATUS_LABEL, REVIEW_KIND_LABEL } from '../../hr/performance';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface MyReviewPanelProps {
  readonly reviewsStatus: QueryStatus;
  readonly reviews: readonly Review[] | null;
  readonly reviewsError: WorkforceApiError | null;
  readonly onReloadReviews: () => void;

  readonly goalsStatus: QueryStatus;
  readonly goals: readonly Goal[] | null;
  readonly goalsError: WorkforceApiError | null;
  readonly onReloadGoals: () => void;

  readonly submitPending: boolean;
  readonly submitError: WorkforceApiError | null;
  readonly submitted: Review | null;
  /** `kind`, and any `employee_id`, are decided by this panel — never by the caller. */
  readonly onSubmitSelfReview: (body: ReviewSubmitRequest) => void;
}

export function MyReviewPanel(props: MyReviewPanelProps): ReactNode {
  const { t } = useI18n();
  const [cycleId, setCycleId] = useState('');
  const [overall, setOverall] = useState('');
  const [narrative, setNarrative] = useState('');
  const [scoreId, setScoreId] = useState('');

  const ready = cycleId.trim() !== '';

  return (
    <section className="wp-section" aria-labelledby="w8-my-heading" data-testid="w8-my">
      <h2 className="wp-section__title" id="w8-my-heading">
        {t('hr.performance.my.title')}
      </h2>

      {/* ── My reviews ──────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w8-my-reviews">
        <h3 className="wp-section__title">{t('hr.performance.myReviews.title')}</h3>
        {props.reviewsStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('hr.performance.myReviews.subject')}
            testId="w8-my-reviews-skeleton"
          />
        ) : null}
        {props.reviewsStatus === 'error' && props.reviewsError !== null ? (
          <ApiErrorNote
            error={props.reviewsError}
            onRetry={props.onReloadReviews}
            subject={t('hr.performance.myReviews.subject')}
            testId="w8-my-reviews-error"
          />
        ) : null}
        {props.reviews === null ? null : props.reviews.length === 0 ? (
          <EmptyState
            title={t('hr.performance.myReviews.empty.title')}
            description={t('hr.performance.myReviews.empty.body')}
            testId="w8-my-reviews-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w8-my-reviews-list">
            {props.reviews.map((review) => (
              <li className="wp-row" key={review.id} data-testid={`w8-my-review-${review.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity="info"
                      icon={SEVERITY_ICON.info}
                      label={t(REVIEW_KIND_LABEL[review.kind])}
                      testId={`w8-my-review-kind-${review.id}`}
                    />
                  </p>
                  <p className="wp-small wp-muted">
                    {review.overall === null
                      ? t('hr.performance.myReviews.row.noOverall')
                      : t('hr.performance.myReviews.row.overall', { overall: review.overall })}
                  </p>
                  <p className="wp-small wp-muted">
                    {review.narrative ?? t('hr.performance.myReviews.row.noNarrative')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── My goals ────────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w8-my-goals">
        <h3 className="wp-section__title">{t('hr.performance.myGoals.title')}</h3>
        {props.goalsStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('hr.performance.myGoals.subject')}
            testId="w8-my-goals-skeleton"
          />
        ) : null}
        {props.goalsStatus === 'error' && props.goalsError !== null ? (
          <ApiErrorNote
            error={props.goalsError}
            onRetry={props.onReloadGoals}
            subject={t('hr.performance.myGoals.subject')}
            testId="w8-my-goals-error"
          />
        ) : null}
        {props.goals === null ? null : props.goals.length === 0 ? (
          <EmptyState
            title={t('hr.performance.myGoals.empty.title')}
            description={t('hr.performance.myGoals.empty.body')}
            testId="w8-my-goals-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w8-my-goals-list">
            {props.goals.map((goal) => (
              <li className="wp-row" key={goal.id} data-testid={`w8-my-goal-${goal.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={GOAL_STATUS_BADGE[goal.status]}
                      icon={SEVERITY_ICON[GOAL_STATUS_BADGE[goal.status]]}
                      label={t(GOAL_STATUS_LABEL[goal.status])}
                      testId={`w8-my-goal-status-${goal.id}`}
                    />{' '}
                    <span>{goal.title}</span>
                  </p>
                  <p className="wp-small wp-muted">
                    {goal.measure ?? t('hr.performance.myGoals.row.noMeasure')}
                    {goal.weight === null
                      ? ''
                      : ` · ${t('hr.performance.myGoals.row.weight', { weight: goal.weight })}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Submit a self-review ────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w8-self-review">
        <h3 className="wp-section__title">{t('hr.performance.selfReview.title')}</h3>
        <p className="wp-small wp-muted">{t('hr.performance.selfReview.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!ready || props.submitPending) return;
            const trimmedCycle = cycleId.trim();
            const trimmedOverall = overall.trim();
            const trimmedNarrative = narrative.trim();
            const trimmedScore = scoreId.trim();
            props.onSubmitSelfReview({
              cycle_id: trimmedCycle,
              kind: 'self',
              ...(trimmedOverall === '' ? {} : { overall: Number(trimmedOverall) }),
              ...(trimmedNarrative === '' ? {} : { narrative: trimmedNarrative }),
              ...(trimmedScore === '' ? {} : { productivity_score_id: trimmedScore }),
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w8-self-review-cycle">
              {t('hr.performance.cycleIdLabel')}
            </label>
            <input
              className="wp-field__input"
              id="w8-self-review-cycle"
              data-testid="w8-self-review-cycle"
              type="text"
              inputMode="numeric"
              value={cycleId}
              autoComplete="off"
              onChange={(event) => {
                setCycleId(event.target.value);
              }}
            />
            <p className="wp-small wp-muted">{t('hr.performance.cycleIdHint')}</p>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w8-self-review-overall">
              {t('hr.performance.selfReview.overall')}
            </label>
            <input
              className="wp-field__input"
              id="w8-self-review-overall"
              data-testid="w8-self-review-overall"
              type="number"
              min="1"
              max="5"
              value={overall}
              onChange={(event) => {
                setOverall(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w8-self-review-narrative">
              {t('hr.performance.selfReview.narrative')}
            </label>
            <textarea
              className="wp-field__input"
              id="w8-self-review-narrative"
              data-testid="w8-self-review-narrative"
              maxLength={4000}
              value={narrative}
              onChange={(event) => {
                setNarrative(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w8-self-review-score">
              {t('hr.performance.selfReview.productivityScoreId')}
            </label>
            <input
              className="wp-field__input"
              id="w8-self-review-score"
              data-testid="w8-self-review-score"
              type="text"
              inputMode="numeric"
              value={scoreId}
              autoComplete="off"
              onChange={(event) => {
                setScoreId(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w8-self-review-submit"
              disabled={!ready || props.submitPending}
            >
              {props.submitPending
                ? t('hr.performance.selfReview.submitting')
                : t('hr.performance.selfReview.submit')}
            </button>
          </div>
        </form>
        {props.submitError === null ? null : (
          <ApiErrorNote
            error={props.submitError}
            subject={t('hr.performance.selfReview.subject')}
            testId="w8-self-review-error"
          />
        )}
        {props.submitted === null ? null : (
          <StatusNote
            severity="low"
            title={t('hr.performance.selfReview.ok')}
            testId="w8-self-review-ok"
          />
        )}
      </div>
    </section>
  );
}
